import * as React from 'react'
import { FC } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useCheckPermissions } from '@user/store/authorization/permissions'
import { ProtectedRouteWithToast } from './ProtectedRouteWithToast'

import { Login, LoginSSO } from '@user/components/Login'
import { AccessDenied } from '@user/components/AccessDenied'
import ExternalLogin from '../ExternalLogin/ExternalLogin'
import { ImpersonationLogin } from '../ImpersonationLogin'
import { Settings } from '@user/components/Settings'
import { LeadRoutes } from '../Leads'
import { OpportunityRoutes } from '../Opportunities'
import { CalendarRoutes } from '@user/features/UnifiedCalendar'
import { useUnifiedCalendarEnabled } from '@user/store/tenantFeatures/hooks/useUnifiedCalendarEnabled'
import { Locations } from '../Location'
import { DraftLeadRoutes } from '@user/components/DraftLeads/DraftLeadRoutes'
import { DashboardGrid } from '../Dashboard/Grid/Grid'
import { GuestUserDashboard } from '../Dashboard/GuestUserDashboard'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization/userSlice'
import { Unknown } from './Unknown'
import { AccountSettings } from '../Account/AccountSettings'
import LogoutSSO from '../Login/LogoutSSO'
import { HealthCheck } from '../HealthCheck/HealthCheck'

interface Props {
  authenticated: boolean
  isGuestUser?: boolean
}

export const AppRoutes: FC<Props> = ({ authenticated, isGuestUser }) => {
  const { isAdmin } = useSelector(selectCurrentUser)
  const unifiedCalendarEnabled = useUnifiedCalendarEnabled()

  const [canViewLeads, canAddLeads, canViewOpps, canViewPendingLeads] =
    useCheckPermissions(
      'leads_view',
      'leads_add',
      'opportunities_view',
      'pending_leads_view',
    )

  return (
    <Routes>
      {isGuestUser ? (
        <Route
          path="/scdashboard"
          element={
            <ProtectedRouteWithToast isAllowed={authenticated && isGuestUser}>
              <GuestUserDashboard />
            </ProtectedRouteWithToast>
          }
        />
      ) : (
        <Route
          path="/scdashboard"
          element={
            <ProtectedRouteWithToast isAllowed={authenticated && !isGuestUser}>
              <DashboardGrid />
            </ProtectedRouteWithToast>
          }
        />
      )}

      <Route
        path="/pending-leads"
        element={
          <ProtectedRouteWithToast
            isAllowed={canViewPendingLeads && !isGuestUser}
          >
            <DraftLeadRoutes />
          </ProtectedRouteWithToast>
        }
      />
      <Route
        path="/leads/*"
        element={
          <ProtectedRouteWithToast
            isAllowed={canViewLeads || (!isGuestUser ? canAddLeads : true)}
          >
            <LeadRoutes />
          </ProtectedRouteWithToast>
        }
      />
      <Route
        path="/opportunities/*"
        element={
          <ProtectedRouteWithToast isAllowed={canViewOpps && !isGuestUser}>
            <OpportunityRoutes />
          </ProtectedRouteWithToast>
        }
      />
      <Route
        path="/calendar/*"
        element={
          <ProtectedRouteWithToast
            isAllowed={unifiedCalendarEnabled && !isGuestUser}
          >
            <CalendarRoutes />
          </ProtectedRouteWithToast>
        }
      />
      <Route
        path="/locations/*"
        element={
          <ProtectedRouteWithToast isAllowed={false}>
            <Locations />
          </ProtectedRouteWithToast>
        }
      />
      <Route
        path="/settings/*"
        element={
          <ProtectedRouteWithToast isAllowed={isAdmin}>
            <Settings />
          </ProtectedRouteWithToast>
        }
      />

      <Route
        path="/accountSettings"
        element={
          <ProtectedRouteWithToast isAllowed={authenticated}>
            <AccountSettings />
          </ProtectedRouteWithToast>
        }
      />
      {/* authentication routes */}
      <Route path="/impersonate" element={<ImpersonationLogin />} />
      <Route
        path="/external-login"
        element={<ExternalLogin mode="external" />}
      />

      <Route path="/auth-login-sso" element={<ExternalLogin mode="sso" />} />
      <Route path="/" element={<LoginSSO />} />
      <Route path="/login" element={<Login />} />
      <Route path="/logout" element={<LogoutSSO />} />
      <Route path="/access-denied" element={<AccessDenied />} />

      <Route path="/unknown" element={<Unknown />} />
      <Route path="/health/live" element={<HealthCheck />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
AppRoutes.displayName = 'AppRoutes'
